import _ from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import {
  Button,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';

import { calcMileage } from 'helpers/unitCalculations';
import { PitstopButton } from 'shared';

import {
  AppStore,
  CarStore,
  CurrentUserStore,
  IssueStore,
  ShopStore,
} from 'stores';

import {
  CheckOutlined,
  EditOutlined,
  GoogleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import GoogleSearchBtn from './GoogleSearchBtn';
import MarkAsCompleteBtn from './MarkAsCompleteBtn';
import MarkAsCurrentBtn from './MarkAsCurrentBtn';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const PitstopText = styled(Text)`
  font-size: 14px !important;
`;

const SectionTitle = styled(Title)`
  font-size: 16px !important;
  margin-bottom: 1rem !important;
`;

const BlueTitle = styled(Title)`
  font-size: 14px !important;
  color: #5774b4 !important;
`;

const BlueTitleCapitalized = styled(BlueTitle)`
  text-transform: capitalize;
`;

const PriorityWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  margin-bottom: 10px;
`;

const PriorityUpdateForm = styled(Form)`
  display: flex;
  align-items: center;
  height: 100%;
  .ant-form-item {
    align-items: center;
    margin-bottom: 0px;
    margin-right: 4px;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 0px;
    margin-right: 4px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .ant-typography {
    margin: 0px !important;
  }
`;

const priorityTagColors = (val) => {
  switch (val) {
    case -1:
      return '#c4c3c2';
    case 1:
      return '#dec431';
    case 2:
      return '#e28931';
    case 4:
      return '#f92d2e';
  }
  return '#c4c3c2';
};

const priorityOptions = [
  { value: -1, label: 'Not Valid' },
  { value: 1, label: 'Minor' },
  { value: 2, label: 'Major' },
  { value: 4, label: 'Critical' },
];

const IssueProfilePage = observer((props) => {
  const [form] = Form.useForm();
  const issueId = Number(props.match.params.id);

  const [state, setState] = useState({
    editing: false,
    openServiceHistory: false,
    symptomAnalyserReport: null,
    isFetchingSymptomAnalyserReport: false,
    isSavedConfigService: true,
    currentNotesBoxLength: 0,
    troubleshooting: null,
    loadingTroubleshooting: true,
    relevantServices: null,
    applyToSimilar: 'none',
    updatedPriority: 'nochange',
  });

  const issue = IssueStore.data.get(issueId);

  useEffect(() => {
    const disposer = observe(CarStore, 'demo', async () => {
      if (CarStore.demo === false) {
        AppStore.openModals.set('backToDashboard', true);
      }
    });

    const init = async () => {
      AppStore.setSelectedSidebarKey('/issues');
      await getIssueData();
      loadTroubleshooting();

      let relevantServicesResponse;
      if (IssueStore.data.has(String(issueId) + 'relevant-services')) {
        relevantServicesResponse = IssueStore.data.get(
          String(issueId) + 'relevant-services'
        );
      } else {
        relevantServicesResponse = await IssueStore.getRelevantServices(
          issueId,
          issue?.name,
          ShopStore.currentShop.id
        );
      }

      setState((prev) => ({
        ...prev,
        relevantServices: relevantServicesResponse,
        isSavedConfigService:
          !!CurrentUserStore.user.settings.defaultSavedConfigService,
      }));
    };

    init();

    return () => {
      disposer();
    };
  }, []);

  const loadTroubleshooting = async () => {
    const code = issue?.name;
    const fmiCode = _.get(issue, 'fmi.fmiCode');

    if (
      !IssueStore.data.has(
        Number(String(issueId) + String(code) + String(fmiCode))
      )
    ) {
      try {
        setState((prev) => ({ ...prev, loadingTroubleshooting: true }));
        const response = await IssueStore.getIssueTroubleshooting(
          issueId,
          code,
          fmiCode
        );
        if (!response) return;

        setState((prev) => ({
          ...prev,
          troubleshooting: response,
        }));
      } catch (err) {
        AppStore.addError(err.message);
      } finally {
        setState((prev) => ({
          ...prev,
          loadingTroubleshooting: false,
        }));
      }
    } else {
      setState((prev) => ({
        ...prev,
        troubleshooting: IssueStore.data.get(
          Number(String(issueId) + String(code) + String(fmiCode))
        ),
        loadingTroubleshooting: false,
      }));
    }
  };

  const getIssueData = async (force = false, showLoading = true) => {
    if (force || !IssueStore.data.has(issueId) || !issue?.loaded) {
      // if issue is not loaded, load it
      await IssueStore.getIssueById(issueId);
      // check if ShopStore.currentShop.id is not provided or isAdmin

      if (!ShopStore.currentShop.id || ShopStore.currentShop.id === -1) {
        try {
          if (showLoading) {
            AppStore.addLoading('Loading issue data ...');
          }
          const carId = issue?.car.id;
          const carData = await CarStore.getCarByIdAndReturn(carId);
          let shopId = carData.shop.id;
          await IssueStore.getIssueById(issueId, shopId);
        } catch (err) {
          AppStore.addError(err.message);
        } finally {
          if (showLoading) {
            AppStore.removeLoading('Loading issue data ...');
          }
        }
      }
    }
  };

  const onUpdate = async (values) => {
    let updateIssueObject = {};

    _.each(values, (value, key) => {
      if (!_.isNil(value)) {
        updateIssueObject[key] = value;
      }
      if (updateIssueObject['priority'] === 0) {
        updateIssueObject['priority'] = 1;
      }
    });

    if (state.updatedPriority !== 'nochange') {
      updateIssueObject['priority'] = parseInt(state.updatedPriority);
    }

    if (state.applyToSimilar === 'entire_fleet') {
      updateIssueObject['applyToSimilarIssues'] = true;
      updateIssueObject['applyToMMYConfig'] = '';
    } else if (state.applyToSimilar !== 'none') {
      updateIssueObject['applyToMMYConfig'] = state.applyToSimilar; // 'm', 'mm', or 'mmy'
      updateIssueObject['applyToSimilarIssues'] = false;
    }

    try {
      AppStore.addLoading('Updating the issue ...');
      await issue.update(updateIssueObject);
      await getIssueData();
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      AppStore.removeLoading('Updating the issue ...');
      setState((prev) => ({
        ...prev,
        editing: false,
        isSavedConfigService:
          !!CurrentUserStore.user.settings.defaultSavedConfigService,
      }));
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      let _values = {
        shopId: ShopStore.currentShop.id,
        userId: CurrentUserStore.user.id,
        item: issue.item,
        status: issue.status,
        source: issue.source,
      };

      _.each(values, (value, key) => {
        if (key === 'item') {
          if (_values['report']) _values['report'] = { testName: value };
          _values['item'] = value;
        } else if (key === 'priority') {
          _values['priority'] =
            typeof value === 'string'
              ? parseInt(values.priority)
              : parseInt(issue.priority);
        } else {
          _values[key] = value;
        }
      });

      await onUpdate(_values);
    } catch (err) {
      AppStore.addError('Validation failed:', err);
    }
  };

  const getVMRSCode = (item) => {
    const vmrsCodes = {
      Battery: '032',
      Brakes: '013',
      'DEF fluid': '043',
      'Air Flow': '041',
    };
    return vmrsCodes[item] || '900';
  };

  const getVMRSText = (item) => {
    const vmrsTexts = {
      Battery: 'CRANKING SYSTEM',
      Brakes: 'BRAKE SYSTEM',
      'DEF fluid': 'EXHAUST SYSTEM',
      'Air Flow': 'AIR INTAKE SYSTEM',
    };
    return vmrsTexts[item] || 'OTHER';
  };

  const getTroubleshooting = (troubleshooting) => {
    return _.get(troubleshooting, 'description', 'Not Available');
  };

  const onSaveNotes = async () => {
    try {
      const values = await form.validateFields();
      let _values = {
        shopId: ShopStore.currentShop.id,
        userId: CurrentUserStore.user.id,
        item: issue.item,
        status: issue.status,
        source: issue.source,
        notes: values.notes,
      };

      _.each(values, (value, key) => {
        if (key === 'item') {
          if (_values['report']) _values['report'] = { testName: value };
          _values['item'] = value;
        } else if (key === 'priority') {
          _values['priority'] =
            typeof value === 'string'
              ? parseInt(values.priority)
              : parseInt(issue.priority);
        } else {
          _values[key] = value;
        }
      });

      await onUpdate(_values);
    } catch (err) {
      AppStore.addError('Validation failed:', err);
    }
  };

  const onClickSavePriority = async () => {
    if (!state.editing) {
      return setState((prev) => ({ ...prev, editing: true }));
    }

    try {
      const values = await form.validateFields();
      let _values = {
        shopId: ShopStore.currentShop.id,
        userId: CurrentUserStore.user.id,
        item: issue.item,
        status: issue.status,
        source: issue.source,
      };

      _.each(values, (value, key) => {
        if (key === 'item') {
          if (_values['report']) _values['report'] = { testName: value };
          _values['item'] = value;
        } else if (key === 'priority') {
          _values['priority'] =
            typeof value === 'string'
              ? parseInt(values.priority)
              : parseInt(issue.priority);
        } else {
          _values[key] = value;
        }
      });

      await onUpdate(_values);
      setState((prev) => ({ ...prev, editing: false }));
    } catch (err) {
      AppStore.addError('Validation failed:', err);
    }
  };

  const parseTroubleshootingDescrition = (text) => {
    // eslint-disable-next-line no-useless-escape
    const regex = /\b[A-Z\s\/,-]+:(.*?)(?=\b[A-Z\s\/,-]+:|$)/gs;
    let match;
    let result = [];
    const processedText = text.replaceAll('\\n', ' ');

    while ((match = regex.exec(processedText)) !== null) {
      const [fullMatch, content] = match;
      const title = fullMatch.slice(0, -content.length - 1);
      result.push({ title: title.trim(), content: content.trim() });
    }

    return result;
  };

  const parseManufacturerSpecificDisclaimer = (issue) => {
    if (!_.get(issue, 'partialMatch')) {
      if (_.get(issue, 'engineMake')) {
        return `The meaning of this fault code is specific to this vehicle's engine, a ${_.get(
          issue,
          'engineMake'
        )} ${_.get(issue, 'engineModel')}.`;
      }
      return `The meaning of this fault code is specific to the this type of vehicle, a ${_.get(
        issue,
        'vehicleMake'
      )} ${_.get(issue, 'vehicleModel')}.`;
    }

    if (_.get(issue, 'engineMake')) {
      return `The meaning of this fault code is specific to the vehicle's engine manufacturer. This vehicle's engine is a ${_.get(
        issue,
        'engineMake'
      )} ${_.get(issue, 'engineModel')}, our closest match is for a ${_.get(
        issue,
        'engineMake'
      )} ${_.get(issue, 'codeEngineModel')}.`;
    }

    return `The meaning of this fault code is specific to the vehicle manufacturer. This vehicle is a ${_.get(
      issue,
      'vehicleMake'
    )} ${_.get(issue, 'vehicleModel')}, our closest match is a ${_.get(
      issue,
      'vehicleMake'
    )} ${_.get(issue, 'codeVehicleModel')}.`;
  };

  if (_.isNil(issue)) {
    return (
      <>
        <h1>Issue Profile</h1>
        <Spin />
      </>
    );
  }

  let { priority, source, status, car, notes, item, triggeredAt, vmrs } = issue;

  const { troubleshooting, loadingTroubleshooting } = state;
  if (['heavyVehicleDTC', 'lightVehicleDTC', 'dtc'].indexOf(source) !== -1) {
    source = 'DTC';
  }
  const troubleshootingSections = parseTroubleshootingDescrition(
    getTroubleshooting(troubleshooting)
  );
  const hasFMI =
    issue.getFailureModeIndicator() &&
    !String(issue.getFailureModeIndicator()).includes(
      'No available failure mode indicator'
    );
  const isIssueComplete = issue.status === 'done';
  const hasExplanation =
    issue.getExplanation() &&
    !String(issue.getExplanation()).includes('No available explanation');
  const hasChatGpt = !!issue.gptDescription;
  const dieselRepairPunchoutUrl = 'https://app-repair.diesellaptops.com/#/';

  const createDieselRepairPunchoutUrl = (moduleName) => {
    const { getItem, car } = issue;
    return `${dieselRepairPunchoutUrl}${moduleName}?src=pits&VIN=${car.vin}&code=${getItem()}`;
  };

  return (
    <React.Fragment>
      <Row>
        <Col span={18}>
          <Title style={{ fontSize: '24px' }}>{issue.getName()}</Title>
          <p style={{ fontSize: '14px' }}>
            Vehicle: <Link to={`/car/${car.id}`}>{car.name || car.vin}</Link>
            <Text style={{ marginLeft: 10 }}>
              Current Mileage:{' '}
              {`${Math.round(calcMileage(car.mileageTotal)).toReadable()} ${CurrentUserStore.user.displayedOdometer}`}
            </Text>
          </p>
          <PriorityWrapper>
            {!_.isNil(priority) && state.editing ? (
              <PriorityUpdateForm onSubmit={onSubmit}>
                <Form.Item
                  name="priority"
                  rules={[
                    { required: true, message: 'Please select priority!' },
                  ]}
                  initialValue={issue.priority}
                  label="Priority"
                >
                  <Select
                    style={{ width: 110 }}
                    onChange={(value) => {
                      setState((prev) => ({ ...prev, updatedPriority: value }));
                      form.setFieldsValue({ priority: value });
                    }}
                    placeholder="Priority"
                    optionRender={({ value, label }) => (
                      <Tag color={priorityTagColors(value)}>{label}</Tag>
                    )}
                    labelRender={({ value, label }) => (
                      <Tag color={priorityTagColors(value)}>{label}</Tag>
                    )}
                    options={priorityOptions}
                  />
                </Form.Item>
                <Form.Item
                  name="applyToSimilar"
                  label="Apply to"
                  initialValue="none"
                  rules={[
                    {
                      required: true,
                      message: 'Please select application scope!',
                    },
                  ]}
                >
                  <Select
                    style={{ width: 180 }}
                    dropdownStyle={{ minWidth: 190 }}
                    onChange={(value) => {
                      setState((prev) => ({ ...prev, applyToSimilar: value }));
                      form.setFieldsValue({ applyToSimilar: value });
                    }}
                    placeholder="Only This Issue"
                  >
                    <Select.Option value="none">
                      <Tag>Only This Issue</Tag>
                    </Select.Option>
                    <Select.Option value="id_car">
                      <Tag>This Vehicle</Tag>
                    </Select.Option>
                    <Select.Option value="mmy">
                      <Tag>Same Make, Model, Year</Tag>
                    </Select.Option>
                    <Select.Option value="mm">
                      <Tag>Same Make & Model</Tag>
                    </Select.Option>
                    <Select.Option value="m">
                      <Tag>Same Make</Tag>
                    </Select.Option>
                    <Select.Option value="entire_fleet">
                      <Tag>Entire Fleet</Tag>
                    </Select.Option>
                  </Select>
                </Form.Item>
              </PriorityUpdateForm>
            ) : (
              <Tag color={IssueStore.data.get(issueId).priorityTagColor}>
                {IssueStore.data.get(issueId).priorityCategory}
              </Tag>
            )}
            <Button
              shape="circle"
              icon={state.editing ? <CheckOutlined /> : <EditOutlined />}
              onClick={onClickSavePriority}
              style={{ marginRight: 10 }}
            />
            {status === 'new' && (
              <MarkAsCompleteBtn
                issue={issue}
                onClick={() => getIssueData(true, false)}
              />
            )}
            {status === 'waitingForApproval' && (
              <MarkAsCompleteBtn
                issue={issue}
                onClick={() => getIssueData(true, false)}
              />
            )}
            {['done', 'waitingForApproval', 'upcoming'].includes(status) && (
              <MarkAsCurrentBtn
                issue={issue}
                onClick={() => getIssueData(true, false)}
              />
            )}
          </PriorityWrapper>
        </Col>
      </Row>
      <Divider />

      {/* Details Section */}
      <Row style={{ marginBottom: 10 }}>
        <Col span={24}>
          <Collapse defaultActiveKey={['1']} expandIconPosition="right">
            <Panel
              header={
                <PanelHeader>
                  <SectionTitle level={2}>Details</SectionTitle>
                </PanelHeader>
              }
              key="1"
            >
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                <Col span={7}>
                  <BlueTitle level={3}>Description</BlueTitle>
                  <PitstopText>{issue.getDescription()}</PitstopText>
                  <Row style={{ margin: '1rem 0 2rem' }}>
                    <Col span={24}>
                      <GoogleSearchBtn
                        icon={<GoogleOutlined />}
                        issue={issue}
                        type="primary"
                        title="Learn more"
                        style={{ fontSize: '14px' }}
                      />
                    </Col>
                  </Row>
                  {hasExplanation &&
                    (hasChatGpt ? (
                      <>
                        <BlueTitle level={3}>Explanation</BlueTitle>
                        <PitstopText>{issue.gptDescription}</PitstopText>
                        <Text
                          type="secondary"
                          style={{
                            display: 'block',
                            fontSize: 13,
                            marginTop: 5,
                          }}
                        >
                          Powered by ChatGPT
                        </Text>
                      </>
                    ) : (
                      <>
                        <BlueTitle level={3}>Explanation</BlueTitle>
                        <PitstopText>{issue.getExplanation()}</PitstopText>
                      </>
                    ))}
                </Col>
                <Col offset={5} span={12}>
                  {isIssueComplete && (
                    <>
                      <BlueTitle level={3}>Feedback</BlueTitle>
                      <PitstopText>{issue.feedback || 'N/A'}</PitstopText>
                    </>
                  )}
                  <BlueTitle level={3}>Fault Code</BlueTitle>
                  <PitstopText>{issue.getItem()}</PitstopText>
                  <BlueTitle level={3}>VMRS</BlueTitle>
                  <PitstopText>
                    System -{' '}
                    {source === 'recall'
                      ? 'OTHER'
                      : vmrs
                        ? vmrs.text
                        : getVMRSText(item)}{' '}
                    (
                    {source === 'recall'
                      ? 900
                      : vmrs
                        ? vmrs.sys
                        : getVMRSCode(item)}
                    )
                  </PitstopText>
                  {hasFMI && (
                    <>
                      <BlueTitle level={3}>FMI</BlueTitle>
                      <PitstopText>
                        {issue.getFailureModeIndicator()}
                      </PitstopText>
                    </>
                  )}
                  <BlueTitle level={3}>Fault Code Trigger Count</BlueTitle>
                  <PitstopText>
                    {triggeredAt ? triggeredAt.length : 0} times
                  </PitstopText>
                </Col>
              </Row>

              {/* Suggested Actions */}
              <Row>
                <Col span={24}>
                  <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                    <Col span={7}>
                      <BlueTitle level={3}>Suggested Action</BlueTitle>
                      <PitstopText>{issue.getSuggestedActions()}</PitstopText>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {/* Manufacturer Specific Disclaimer */}
              {_.get(issue, 'partialMatch') && (
                <Row>
                  <Col span={24}>
                    <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                      <Col span={7}>
                        <BlueTitle level={3}>Notice</BlueTitle>
                        <PitstopText>
                          {parseManufacturerSpecificDisclaimer(issue)}
                        </PitstopText>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}

              {/* Relevant Services */}
              {!_.isEmpty(state.relevantServices) && (
                <Row>
                  <Button
                    type="primary"
                    onClick={() =>
                      props.history.push(
                        `/relevant-services?issueId=${issueId}&code=${issue.name}`
                      )
                    }
                  >
                    Relevant Service Records
                  </Button>
                </Row>
              )}
            </Panel>
          </Collapse>
        </Col>
      </Row>

      {/* Troubleshooting Section */}
      {loadingTroubleshooting && (
        <Row style={{ marginBottom: 10 }}>
          <Col span={24}>
            <Skeleton active title paragraph />
          </Col>
        </Row>
      )}

      {!loadingTroubleshooting &&
        troubleshootingSections &&
        troubleshootingSections.length > 0 && (
          <Row style={{ marginBottom: 10 }}>
            <Col span={24}>
              <Collapse expandIconPosition="right" defaultActiveKey={['1']}>
                <Panel
                  header={
                    <PanelHeader>
                      <SectionTitle level={2}>Troubleshooting</SectionTitle>
                    </PanelHeader>
                  }
                  key="1"
                >
                  <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                    {troubleshootingSections.map((section, i) => (
                      <React.Fragment key={i}>
                        <Col span={24}>
                          <BlueTitleCapitalized level={3}>
                            {_.chain(section.title).toLower().value()}
                          </BlueTitleCapitalized>
                          <PitstopText>{section.content}</PitstopText>
                        </Col>
                      </React.Fragment>
                    ))}
                  </Row>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        )}

      {/* Diesel Laptops Section */}
      <Row style={{ marginBottom: 15 }}>
        <Col span={24}>
          <Collapse expandIconPosition="right" defaultActiveKey={['1']}>
            <Panel
              header={
                <PanelHeader>
                  <SectionTitle level={2}>
                    <img
                      alt="Diesel Laptops Logo"
                      src="https://app-repair.diesellaptops.com/icons/favicon-128x128.png"
                      style={{ width: '30px', marginRight: '1rem' }}
                    />
                    Learn More About This Fault Code on Diesel Laptops
                  </SectionTitle>
                </PanelHeader>
              }
              key="1"
            >
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                <Col span={24}>
                  <Button
                    type="link"
                    target="_blank"
                    href={createDieselRepairPunchoutUrl('truck-fault-codes-v3')}
                  >
                    <LinkOutlined />
                    Fault Code Info
                  </Button>
                  <Button
                    type="link"
                    target="_blank"
                    href={createDieselRepairPunchoutUrl('diesel-docs')}
                  >
                    <LinkOutlined />
                    Repair Documentation
                  </Button>
                  <Button
                    type="link"
                    target="_blank"
                    href={createDieselRepairPunchoutUrl('diesel-wiring')}
                  >
                    <LinkOutlined />
                    Wiring Diagrams
                  </Button>
                  <Button
                    type="link"
                    target="_blank"
                    href={createDieselRepairPunchoutUrl('truck-labor-times-v2')}
                  >
                    <LinkOutlined />
                    Labor Time Guides
                  </Button>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Col>
      </Row>

      {/* Fault Code History */}
      <Row style={{ marginBottom: 15 }}>
        <Col span={24}>
          <Collapse expandIconPosition="right">
            <Panel
              header={
                <PanelHeader>
                  <SectionTitle level={2}>Fault Code History</SectionTitle>
                </PanelHeader>
              }
              key="1"
            >
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
                <Col span={24}>
                  <Table
                    loading={_.isNil(triggeredAt)}
                    dataSource={
                      _.isNil(triggeredAt)
                        ? []
                        : _.map(triggeredAt, (val, i) => ({
                            key: i,
                            time: val,
                          }))
                    }
                    columns={[
                      {
                        title: 'Time',
                        dataIndex: 'time',
                        key: 'time',
                        sorter: (a, b) => a > b,
                        render: (item) =>
                          moment
                            .tz(item, CurrentUserStore.user.settings.timezone)
                            .format('ll HH:mm:ss:SSS'),
                      },
                    ]}
                    pagination={{ pageSize: 5 }}
                  />
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Col>
      </Row>

      {/* Notes Section */}
      <Row>
        <Col span={24}>
          <SectionTitle level={2}>Notes</SectionTitle>
          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 20]}>
            <Col span={24}>
              <StyledForm onSubmit={onSubmit} form={form}>
                <Form.Item
                  name="applyToSimilarNotes"
                  label="Apply to"
                  initialValue="none"
                  rules={[
                    {
                      required: true,
                      message: 'Please select application scope!',
                    },
                  ]}
                >
                  <Select
                    style={{ width: 140 }}
                    onChange={(value) => {
                      setState((prev) => ({ ...prev, applyToSimilar: value }));
                      form.setFieldsValue({ applyToSimilarNotes: value });
                    }}
                    placeholder="Select scope"
                  >
                    <Select.Option value="none">
                      <Tag color="#c4c3c2">Only this issue</Tag>
                    </Select.Option>
                    <Select.Option value="m">
                      <Tag color="#c4c3c2">Same Make</Tag>
                    </Select.Option>
                    <Select.Option value="mm">
                      <Tag color="#c4c3c2">Same Make, Model</Tag>
                    </Select.Option>
                    <Select.Option value="mmy">
                      <Tag color="#c4c3c2">Same Make, Model, Year</Tag>
                    </Select.Option>
                    <Select.Option value="id_car">
                      <Tag color="#c4c3c2">This Vehicle</Tag>
                    </Select.Option>
                    <Select.Option value="entire_fleet">
                      <Tag color="#dec431">Entire Fleet</Tag>
                    </Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="notes"
                  initialValue={
                    notes ||
                    'Here you can leave a note that will appear next time this issue occurs.'
                  }
                  rules={[
                    {
                      max: 600,
                      message: 'Note cannot exceed 600 characters',
                    },
                  ]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    maxLength={600}
                    onChange={(e) => {
                      const value = e.target.value;
                      setState((prev) => ({
                        ...prev,
                        currentNotesBoxLength: value.length,
                      }));
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text
                        type={
                          state.currentNotesBoxLength === 600
                            ? 'danger'
                            : 'secondary'
                        }
                      >
                        {state.currentNotesBoxLength}/600
                      </Text>
                    </Col>
                    <Col>
                      <PitstopButton onClick={onSaveNotes}>Save</PitstopButton>
                    </Col>
                  </Row>
                </Form.Item>
              </StyledForm>
            </Col>
          </Row>
        </Col>
      </Row>
    </React.Fragment>
  );
});

IssueProfilePage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default withRouter(IssueProfilePage);
